import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const stored = localStorage.getItem('poshe_cart');
    return stored ? JSON.parse(stored) : [];
  });

  // Persist cart in localStorage
  useEffect(() => {
    localStorage.setItem('poshe_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, color = '') => {
    setCartItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item._id === product._id && item.color === color
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        toast.success('Cart updated!');
        return updated;
      }

      toast.success(`${product.name} added to bag!`);
      return [
        ...prev,
        {
          _id: product._id,
          name: product.name,
          price: product.price,
          mrp: product.mrp,
          image: product.images?.[0] || '',
          color,
          quantity,
          stock: product.stock,
          // Flash sale and bulk discount fields
           salePrice: product.salePrice || null,
          saleStartTime: product.saleStartTime || null,
          saleEndTime: product.saleEndTime || null,
          quantityDiscounts: product.quantityDiscounts || [],
        },
      ];
    });
  };

  const removeFromCart = (productId, color = '') => {
    setCartItems((prev) => {
      const filtered = prev.filter(
        (item) => !(item._id === productId && item.color === color)
      );
      toast.info('Item removed from bag');
      return filtered;
    });
  };

  const updateQuantity = (productId, qty, color = '') => {
    if (qty < 1) return;
    setCartItems((prev) =>
      prev.map((item) =>
        item._id === productId && item.color === color
          ? { ...item, quantity: qty }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('poshe_cart');
  };

  // Calculate effective price for an item considering flash sale + quantity discounts
  const getEffectivePrice = (item) => {
    let price = item.price;

    // Check flash sale
    if (item.salePrice && item.saleEndTime && new Date(item.saleEndTime) > new Date()) {
      if (!item.saleStartTime || new Date(item.saleStartTime) <= new Date()) {
        price = item.salePrice;
      }
    }

    // Check quantity discounts
    if (item.quantityDiscounts && item.quantityDiscounts.length > 0) {
      const sorted = [...item.quantityDiscounts].sort((a, b) => b.minQty - a.minQty);
      const applicable = sorted.find((d) => item.quantity >= d.minQty);
      if (applicable) {
        price = Math.round(price * (1 - applicable.discountPercent / 100));
      }
    }

    return price;
  };

  // Get bulk discount info for an item
  const getBulkDiscount = (item) => {
    if (!item.quantityDiscounts || item.quantityDiscounts.length === 0) return null;
    const sorted = [...item.quantityDiscounts].sort((a, b) => b.minQty - a.minQty);
    return sorted.find((d) => item.quantity >= d.minQty) || null;
  };

  const total = cartItems.reduce(
    (sum, item) => sum + getEffectivePrice(item) * item.quantity,
    0
  );

  const count = cartItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        total,
        count,
        getEffectivePrice,
        getBulkDiscount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
